/* eslint-disable complexity */
import { useMemo, useState, useEffect, useContext, memo, useCallback } from 'react';
import { InteractionStepTaskComponent } from 'react-scale/components/scalers/tasks/ThreadedInteraction/InteractionStep';
import {
  DEFAULT_TOOL_USE_PROCESS_SUPERVISION_SUBTITLE,
  DEFAULT_TOOL_USE_PROCESS_SUPERVISION_TITLE,
  InteractionStepType,
  ToolUseProcessSupervisionStepResponse,
} from '@scale/shared/types/chat';
import {
  useAllResponsesForStepType,
  useMostRecentResponse,
  useMostRecentResponseForStepType,
  useStepParams,
  useStepResponse,
  useChatStoreExtract,
} from '../../../../Chat/hooks/ChatStore.hooks';
import {
  StepWidth,
  StepWidthWrapper,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/step/StepWidthWrapper';
import { StepCard } from 'react-scale/components/scalers/tasks/ThreadedInteraction/step/StepCard';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { CommentButton } from 'react-scale/components/scalers/tasks/ThreadedInteraction/feedback/CommentButton';
import { PinButton } from 'react-scale/components/scalers/tasks/ThreadedInteraction/step/PinButton';
import { ChatMessageDisplayConfigButton } from 'react-scale/components/scalers/tasks/ThreadedInteraction/step/ChatMessageDisplayConfig';
import { useConfigureChatMessageDisplay } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useConfigureChatMessageDisplay';
import { Button, Icon, SelectMenu, Typography } from '@scale/scaleui';
import { constructStepChildId } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useWorkerCommentsStore';
import { useStepSubmitHandler } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useStepSubmitHandler';
import {
  TOOL_USE_PROCESS_SUPERVISION_CONFIG_OPTIONS,
  ToolUseProcessSupervisionConfigId,
  ToolUseProcessSupervisionActionWithUserActions,
} from '@scale/shared/types/chat/toolUseProcessSupervision';
import ToolUseProcessSupervisionAction from './ToolUseProcessSupervisionAction';
import ToolUseProcessSupervisionActionPlan from './ToolUseProcessSupervisionActionPlan';
import { getShouldResetHistory } from './helpers';

type State = 'ACTION_PLAN' | 'ACTION';

// Detects whether the last action is finish
const getIsComplete = (
  actions: ToolUseProcessSupervisionStepResponse['context']['actions'],
  isManuallyMarkedComplete: boolean | null,
  doesActionTriggerCompletion?: (
    actions: ToolUseProcessSupervisionStepResponse['context']['actions'],
  ) => boolean,
) => {
  // if any of the actions chunks have triggersCompletion set to true, then we consider the step complete
  if (doesActionTriggerCompletion) {
    const hasTriggeredCompletion = doesActionTriggerCompletion(actions);
    if (hasTriggeredCompletion) {
      return true;
    }
  }

  if (isManuallyMarkedComplete) {
    return true;
  }

  if (actions.length === 0) {
    return false;
  }

  const lastAction = actions[actions.length - 1];
  const actionSubchunk = (lastAction.chunks || []).find(subchunk => subchunk.name === 'Action');
  if (!actionSubchunk) {
    return false;
  }
  const actionValue = actionSubchunk.rewriteValue || actionSubchunk.originalValue;
  return actionValue.toLowerCase() === 'finish';
};

const findConfigOption = (configId: ToolUseProcessSupervisionConfigId) => {
  const config = TOOL_USE_PROCESS_SUPERVISION_CONFIG_OPTIONS.find(option => option.id === configId);
  if (!config) {
    throw new Error('ToolUseProcessSupervision: No config found');
  }
  return config;
};

const ToolUseProcessSupervisionComponent: InteractionStepTaskComponent = ({
  readOnly,
  responseIndex,
  isPinnedView,
}) => {
  const [actionPlan, setActionPlan] =
    useState<Partial<ToolUseProcessSupervisionStepResponse['context']['actionPlan']>>();
  const [actions, setActions] = useState<ToolUseProcessSupervisionActionWithUserActions[]>([]);
  const filteredActions = actions.filter(action => action.userDeleted !== true);
  const markStepAsIncompletedByIndex = useChatStoreExtract(
    state => state.markStepAsIncompletedByIndex,
  );

  // NOTE: Loading data
  const stepParams = useStepParams<InteractionStepType.ToolUseProcessSupervision>(responseIndex);

  const runToolOnlyReviewLevels = stepParams.params.run_tool_only_review_levels;
  const reviewLevel = useChatStoreExtract(state => state.reviewLevel);
  const isRunToolOnly = runToolOnlyReviewLevels?.includes(reviewLevel) || false;

  const getSetAction =
    ({ actionIdx }: { actionIdx: number }) =>
    (newAction: ToolUseProcessSupervisionActionWithUserActions) => {
      setActions(
        actions.map((action, idx) => {
          if (idx === actionIdx) {
            return newAction;
          }
          return action;
        }),
      );
    };
  const createThoughtChunk = () => ({
    name: 'Thought',
    type: 'markdown' as const,
    originalValue: 'Empty thought (added by user)',
    rewriteValue: null,
    rating: null,
    readOnly: isRunToolOnly,
  });

  const createActionChunks = () => [
    {
      name: 'Action',
      type: 'tool_select' as const,
      originalValue: 'no_tool_selected',
      rewriteValue: null,
      rating: null,
      readOnly: isRunToolOnly,
    },
    {
      name: 'Action Input',
      type: 'tool_params_json' as const,
      originalValue: '{}',
      rewriteValue: null,
      rating: null,
      readOnly: isRunToolOnly,
    },
  ];

  const createThoughtAndActionChunks = () => [createThoughtChunk(), ...createActionChunks()];

  const addThought = () => {
    const newAction = {
      chunks: [createThoughtChunk()],
      userAdded: true,
    };
    setActions([...actions, newAction]);
  };

  const addAction = () => {
    const newAction = {
      chunks: createActionChunks(),
      userAdded: true,
    };
    setActions([...actions, newAction]);
  };

  const addThoughtAction = () => {
    setActions([...actions, { chunks: createThoughtAndActionChunks(), userAdded: true }]);
  };

  const deleteAction = (actionIdx: number) => {
    if (filteredActions.length <= 1) {
      alert(
        "You can't delete your only action. Please mark it as incorrect and rewrite it instead.",
      );
      return;
    }
    const action = actions[actionIdx];
    if (action.userAdded === true) {
      // User added actions are hard deleted from the list
      setActions(actions.filter((_, idx) => idx !== actionIdx));
    } else {
      // Model added actions are soft deleted by setting the userDeleted flag
      // This allows the frontend to show a "Restore Action" button
      // and to appropriately show the diff view
      action.userDeleted = true;
      setActions([...actions]);
    }
  };

  const toolIds = useMemo(() => {
    // if it's configured, use those
    if (stepParams.params.tool_ids) {
      if (typeof stepParams.params.tool_ids === 'string') {
        try {
          return JSON.parse(stepParams.params.tool_ids);
        } catch (e) {
          return stepParams.params.tool_ids;
        }
      }
      return stepParams.params.tool_ids;
    }
    // if not, revert to the default from the preset, for max backwards compatibility
    const config = findConfigOption(stepParams.params.configId);
    return config.toolIds || [];
  }, [stepParams.params.tool_ids, stepParams.params.configId]);

  const mrsOutput = useMostRecentResponse(responseIndex);
  const prevToolUseProcessSupervisionResponse = useMostRecentResponseForStepType(
    InteractionStepType.ToolUseProcessSupervision,
    responseIndex - 1,
  );
  const allToolUseProcessSupervisionResponses = useAllResponsesForStepType(
    InteractionStepType.ToolUseProcessSupervision,
  );

  const allActionsAcrossTurns = useMemo(
    () =>
      [
        ...allToolUseProcessSupervisionResponses.map(response => response.context.actions),
        actions,
      ].filter(Boolean),
    [allToolUseProcessSupervisionResponses, actions],
  );

  const response = useStepResponse<ToolUseProcessSupervisionStepResponse>(responseIndex);
  const taskContext = useContext(ChatTaskContext);
  const {
    chatMessageDisplayConfig,
    useMarkdownDisplay,
    updateMarkdownConfig,
    toggleChatMessageDisplayType,
  } = useConfigureChatMessageDisplay();

  const { onSubmit } = useStepSubmitHandler({
    responseIndex,
  });

  // allows the contributor to manually marked that the current prompt is solved, so we can allow another prompt in the next MultiTurnContinue
  // take the existing response.context value (what was submitted)
  const [isManuallyMarkedComplete, setIsManuallyMarkedComplete] = useState<boolean | null>(
    response?.context?.isComplete === null ? null : !!response?.context?.isComplete,
  );

  const submit = () => {
    // TODO: (@averyyip) Add submit logic for actions
    if (!actionPlan && stepParams.params.isActionPlanEnabled) {
      throw new Error('No action plan found while attempting to save');
    }

    const config = findConfigOption(stepParams.params.configId);

    const output = filteredActions.length
      ? config.action.unparse(filteredActions)
      : stepParams.params.isActionPlanEnabled
      ? // TODO(@averyyip): Fix typing
        // @ts-ignore
        config.actionPlan.unparse(actionPlan)
      : '';

    const isComplete = getIsComplete(
      filteredActions,
      isManuallyMarkedComplete,
      config?.doesActionTriggerCompletion,
    );

    const originalActions = actions.filter(action => action.userAdded !== true);

    const responseToSubmit: ToolUseProcessSupervisionStepResponse = {
      type: InteractionStepType.ToolUseProcessSupervision,
      context: {
        isComplete,
        // TODO(@averyyip): Fix typing
        // @ts-ignore
        actionPlan,
        actions: filteredActions,
        originalActions,
        actionsState: actions,
      },
      output,
    };
    const shouldResetHistory = getShouldResetHistory(response, responseToSubmit);

    onSubmit(responseToSubmit, responseIndex, stepParams, shouldResetHistory, false);
  };

  const initActionPlan = useCallback(() => {
    if (response?.context.actionPlan) {
      setActionPlan(response.context.actionPlan);
      return;
    } else if (prevToolUseProcessSupervisionResponse) {
      setActionPlan(prevToolUseProcessSupervisionResponse.context.actionPlan);
      return;
    }

    if (!mrsOutput) {
      return;
    }

    const configOption = findConfigOption(stepParams.params.configId);
    const actionPlan = configOption.actionPlan.parse(mrsOutput);
    setActionPlan(actionPlan);
  }, [response, prevToolUseProcessSupervisionResponse, stepParams.params.configId, mrsOutput]);

  const initActions = useCallback(() => {
    if (response?.context.actionsState) {
      setActions(response.context.actionsState);
      return;
    }

    // Tasks before 4/14/2025 do not have actionsRaw field so we use actions instead
    if (response?.context.actions) {
      const pastActions = response?.context.actions.map(action => ({
        // @ts-ignore (actions saved before 4/14/2025 do not have userAdded field)
        userAdded: false,
        ...action,
      }));
      setActions(pastActions);
      return;
    }

    if (!mrsOutput) {
      return;
    }

    const config = findConfigOption(stepParams.params.configId);
    const actions = config.action.parse(mrsOutput);
    // mrsOutput actions are model generated, so we set userAdded to false
    setActions(actions.map(action => ({ ...action, userAdded: false })));
  }, [response, stepParams.params.configId, mrsOutput]);

  useEffect(() => {
    if (stepParams.params.isActionPlanEnabled) {
      initActionPlan();
      if (prevToolUseProcessSupervisionResponse) {
        initActionPlan();
        initActions();
      }
    } else {
      initActions();
    }
  }, [
    initActionPlan,
    initActions,
    prevToolUseProcessSupervisionResponse,
    response,
    stepParams.params.isActionPlanEnabled,
  ]);

  const { state }: { state: State } = useMemo(() => {
    if (stepParams.params.isActionPlanEnabled && !prevToolUseProcessSupervisionResponse) {
      return { state: 'ACTION_PLAN' };
    }
    return { state: 'ACTION' };
  }, [prevToolUseProcessSupervisionResponse, stepParams.params.isActionPlanEnabled]);

  const { isSubmittable, reasonNotSubmittable } = useMemo(() => {
    let reasonNotSubmittable = '';

    // if we want them to mark whether the original prompt is solved,
    // we need to check that they actually filled out the dropdown select
    if (stepParams.params.enable_manual_mark_as_complete && isManuallyMarkedComplete === null) {
      reasonNotSubmittable = 'Please mark whether the original prompt is solved.';
      return { isSubmittable: false, reasonNotSubmittable };
    }

    if (stepParams.params.isActionPlanEnabled && state === 'ACTION_PLAN') {
      // action plan is always valid if run tool only
      if (isRunToolOnly) {
        return { isSubmittable: true, reasonNotSubmittable };
      }
      const isValidActionPlan =
        !!actionPlan &&
        !!actionPlan.originalValue &&
        !!actionPlan.rating &&
        (actionPlan.rating.fields.length === 0 ||
          !!actionPlan.rating.fields.every(field => !!field.value));
      return {
        isSubmittable: isValidActionPlan,
        reasonNotSubmittable: isValidActionPlan ? null : 'Action plan is not valid',
      };
    } else {
      if (isRunToolOnly) {
        // check that every action has an observation
        const hasObservation = filteredActions.every(action => {
          const { chunks } = action;
          const observation = chunks.find(
            chunk => chunk.name === 'Observation' && chunk.originalValue,
          );
          return !!observation;
        });
        return {
          isSubmittable: hasObservation,
          reasonNotSubmittable: hasObservation
            ? ''
            : 'Please generate an observation for each action.',
        };
      }
      const isValidActions = filteredActions.every(action => {
        const { chunks } = action;
        const observation = chunks.find(
          chunk => chunk.name === 'Observation' && chunk.originalValue,
        );

        // check if observation's .parent.action and .parent.actionInput match the current action and actionInput
        const actionChunk = chunks.find(chunk => chunk.name === 'Action' && chunk.originalValue);
        const actionInputChunk = chunks.find(
          chunk => chunk.name === 'Action Input' && chunk.originalValue,
        );

        if (
          !observation &&
          !stepParams.params.skip_observation &&
          actionChunk &&
          (actionChunk.rewriteValue || actionChunk.originalValue) !== 'no_tool_selected'
        ) {
          reasonNotSubmittable = `Missing Observation. Click "Run Tool" to generate one.`;
          return false;
        }

        // filter for chunks marked incorrect but user did not rewrite
        const incorrectButNoChangeChunks = chunks.filter(
          chunk =>
            chunk.rating?.id === 'incorrect' &&
            (chunk.rewriteValue === null || chunk.rewriteValue === chunk.originalValue),
        );

        if (incorrectButNoChangeChunks.length > 0) {
          const chunk_names = incorrectButNoChangeChunks.map(chunk => chunk.name).join(', ');
          reasonNotSubmittable = `Detected that ${chunk_names} marked as incorrect but are unmodified`;
          return false;
        }

        const observationCorrespondsToCurrentActionAndActionInput =
          observation?.parent?.action ===
            (actionChunk?.rating?.id === 'incorrect'
              ? actionChunk?.rewriteValue
              : actionChunk?.originalValue) &&
          observation?.parent?.actionInput ===
            (actionInputChunk?.rating?.id === 'incorrect'
              ? actionInputChunk?.rewriteValue
              : actionInputChunk?.originalValue);
        if (
          !observationCorrespondsToCurrentActionAndActionInput &&
          !stepParams.params.skip_observation &&
          actionChunk &&
          (actionChunk.rewriteValue || actionChunk.originalValue) !== 'no_tool_selected' &&
          observation?.parent !== undefined
        ) {
          reasonNotSubmittable = `Observation must be re-generated since the action or action input were changed.`;
          return false;
        }

        const observation_response = observation?.rewriteValue || observation?.originalValue;
        if (observation_response && observation?.type === 'json') {
          let observation_json;
          try {
            observation_json = JSON.parse(observation_response);
          } catch (e) {
            reasonNotSubmittable = `Observation has an error: ${e}`;
            return false;
          }
          // Never submit if error is due to input schema errors
          if (observation_json.error) {
            reasonNotSubmittable = `Observation has an error: ${observation_json.error}`;
            return false;
          }
          // Enforce that the tool call response is successful unless explicitly allowed
          if (!stepParams.params.allow_tool_call_response_errors && observation_json.stderr) {
            reasonNotSubmittable = `Observation has an error: ${observation_json.stderr}`;
            return false;
          }
        }

        return chunks.every(chunk => {
          const hasValue = !!(chunk.rewriteValue ?? chunk.originalValue);
          const isReadOnly = chunk.readOnly && hasValue;
          const hasRating = !!chunk.rating;
          const hasFilledFields =
            chunk.rating?.fields.length === 0 ||
            chunk.rating?.fields.every(field => {
              return !!field.value;
            });
          const isNotReadOnly = hasValue && hasRating && hasFilledFields;
          if (!isReadOnly && !isNotReadOnly) {
            reasonNotSubmittable = `${chunk.name} is incomplete.`;
            return false;
          }
          return true;
        });
      });

      const toolSet = new Set();
      allActionsAcrossTurns.forEach(actions => {
        actions.forEach(action => {
          return action.chunks.forEach(chunk => {
            if (chunk.name === 'Action') {
              toolSet.add(chunk.rewriteValue || chunk.originalValue);
            }
          });
        });
      });
      const hasAllCorrectActions = allActionsAcrossTurns.every(actions => {
        return actions.every(action => {
          return action.chunks.every(chunk => {
            // Observations do not have ratings.
            return chunk.name === 'Observation' || chunk.rating?.id === 'correct';
          });
        });
      });

      const hasMetSufficientIncorrectActions =
        !stepParams.params.require_incorrect_rating ||
        !toolSet.has('finish') ||
        !hasAllCorrectActions;

      if (!hasMetSufficientIncorrectActions) {
        reasonNotSubmittable =
          'All ratings MUST NOT be correct. Please check the instruction again for the requirement.';
      }

      const minNumToolsUsed = stepParams.params.min_num_tools_used
        ? Number(stepParams.params.min_num_tools_used)
        : 0;

      // Only check this condition if "finish" action was selected. If not,
      // this hasn't had sufficient steps yet and is vacuously is true.
      const hasMetSufficientNumberOfTools =
        !minNumToolsUsed || !toolSet.has('finish') || toolSet.size >= minNumToolsUsed;
      if (!hasMetSufficientNumberOfTools) {
        reasonNotSubmittable = `You must use at least ${minNumToolsUsed} unique actions/tools/APIs.`;
      }

      return {
        isSubmittable:
          isValidActions && hasMetSufficientIncorrectActions && hasMetSufficientNumberOfTools,
        reasonNotSubmittable,
      };
    }
  }, [
    stepParams.params.enable_manual_mark_as_complete,
    stepParams.params.isActionPlanEnabled,
    stepParams.params.require_incorrect_rating,
    stepParams.params.min_num_tools_used,
    stepParams.params.skip_observation,
    stepParams.params.allow_tool_call_response_errors,
    isManuallyMarkedComplete,
    state,
    actionPlan,
    actions,
    allActionsAcrossTurns,
  ]);

  // TODO: CHAT-1109, Refactor all submission validation into ToolUseProcessSupervisionDescriptor.isResponseValid
  useEffect(() => {
    if (!isSubmittable) {
      markStepAsIncompletedByIndex(responseIndex);
    }
  }, [isSubmittable, markStepAsIncompletedByIndex, responseIndex]);

  if (!mrsOutput) {
    return (
      <div>Unable to show tool use process supervision without a model response selector.</div>
    );
  }

  return (
    <StepWidthWrapper width={isPinnedView ? StepWidth.Full : StepWidth.Responsive}>
      <StepCard readOnly={readOnly}>
        <StepCard.Header>
          <StepCard.HeaderLeft>
            <StepCard.Icon icon="award" />
            <StepCard.HeaderColumn>
              <StepCard.Title>{DEFAULT_TOOL_USE_PROCESS_SUPERVISION_TITLE}</StepCard.Title>
              <StepCard.Subtitle>{DEFAULT_TOOL_USE_PROCESS_SUPERVISION_SUBTITLE}</StepCard.Subtitle>
            </StepCard.HeaderColumn>
          </StepCard.HeaderLeft>
          <StepCard.HeaderRight>
            {!taskContext.enableUnifiedFeedback && (
              <CommentButton
                visible={true}
                childId={constructStepChildId(stepParams)}
                turnIndex={stepParams.turn}
              />
            )}
            <PinButton responseIndex={responseIndex} />
            <ChatMessageDisplayConfigButton
              chatMessageDisplayConfig={chatMessageDisplayConfig}
              useMarkdownDisplay={useMarkdownDisplay}
              updateMarkdownConfig={updateMarkdownConfig}
              toggleChatMessageDisplayType={toggleChatMessageDisplayType}
            />
          </StepCard.HeaderRight>
        </StepCard.Header>
        <StepCard.Body>
          {state === 'ACTION_PLAN' && stepParams.params.isActionPlanEnabled && actionPlan && (
            <ToolUseProcessSupervisionActionPlan
              actionPlan={actionPlan}
              setActionPlan={setActionPlan}
              isRunToolOnly={isRunToolOnly}
            />
          )}
          {state === 'ACTION' && actions && (
            <div className="flex flex-col gap-6">
              {actions.map((action, actionIdx) => (
                <div key={actionIdx}>
                  <ToolUseProcessSupervisionAction
                    action={action}
                    setAction={getSetAction({ actionIdx })}
                    deleteAction={
                      stepParams.params.allow_delete_actions
                        ? () => deleteAction(actionIdx)
                        : undefined
                    }
                    toolIds={toolIds}
                    responseIndex={responseIndex}
                    isRunToolOnly={isRunToolOnly}
                  />
                </div>
              ))}
            </div>
          )}
          {/* a select that has true or false for manually marking whether the original prompt is solved */}
          {stepParams?.params?.enable_manual_mark_as_complete && (
            <ManualCompletionSelector
              isManuallyMarkedComplete={isManuallyMarkedComplete}
              setIsManuallyMarkedComplete={(value: boolean | null) =>
                setIsManuallyMarkedComplete(value)
              }
            />
          )}
        </StepCard.Body>

        <StepCard.Footer className="flex flex-row-reverse gap-2 w-full">
          {!taskContext.previewMode && (
            <Button
              variant="primary"
              onClick={submit}
              disabled={!isSubmittable}
              data-testid="submit-tooluseprocesssupervision-button"
            >
              <Icon variant="white" icon="check" size="sm" />
              Save and Continue
            </Button>
          )}
          {stepParams.params.allow_add_actions && (
            <Button onClick={addThought}>+ Add Thought</Button>
          )}
          {stepParams.params.allow_add_actions && <Button onClick={addAction}>+ Add Action</Button>}
          {stepParams.params.allow_add_thought_and_action && (
            <Button onClick={addThoughtAction}>+ Add Thought and Action</Button>
          )}

          {reasonNotSubmittable && <div>{reasonNotSubmittable}</div>}
        </StepCard.Footer>
      </StepCard>
    </StepWidthWrapper>
  );
};

const ManualCompletionSelector = ({
  isManuallyMarkedComplete,
  setIsManuallyMarkedComplete,
}: {
  isManuallyMarkedComplete: boolean | null;
  setIsManuallyMarkedComplete: (value: boolean | null) => void;
}) => (
  <div className="flex flex-row gap-2 border-t border-neutral-200 pt-4 mt-4 items-center">
    <Typography.Paragraph2 className="mb-2">
      Is the original prompt solved yet?
    </Typography.Paragraph2>
    <SelectMenu
      options={[
        { id: 'true', name: 'True' },
        { id: 'false', name: 'False' },
        { id: 'null', name: ' ' },
      ]}
      selectedOptionId={
        isManuallyMarkedComplete === null ? 'null' : isManuallyMarkedComplete ? 'true' : 'false'
      }
      setSelected={id => {
        if (id === 'true') {
          setIsManuallyMarkedComplete(true);
        } else if (id === 'false') {
          setIsManuallyMarkedComplete(false);
        } else {
          setIsManuallyMarkedComplete(null);
        }
      }}
      className="w-[120px]"
    />
  </div>
);

export default memo(ToolUseProcessSupervisionComponent);
